import { useOnboarding } from '@/contexts/OnboardingContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { X, ArrowRight, ArrowLeft, Package, DollarSign, Shield, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to SwapX!',
    description: 'Your trusted marketplace for buying and selling items with delayed disbursement protection',
    icon: Shield,
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          SwapX is a secure marketplace where buyers and sellers can trade with confidence. 
          Let's take a quick tour to show you how it works!
        </p>
        <div className="grid gap-3">
          <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <Shield className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
            <div>
              <h4 className="font-medium">Delayed Disbursement Protection</h4>
              <p className="text-sm text-muted-foreground">Funds are held securely until delivery is confirmed</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-chart-1/5 rounded-lg border border-chart-1/10">
            <Package className="h-5 w-5 text-chart-1 mt-1 flex-shrink-0" />
            <div>
              <h4 className="font-medium">Easy Listings</h4>
              <p className="text-sm text-muted-foreground">Create listings in minutes with AI assistance</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-chart-2/5 rounded-lg border border-chart-2/10">
            <DollarSign className="h-5 w-5 text-chart-2 mt-1 flex-shrink-0" />
            <div>
              <h4 className="font-medium">Fair Pricing</h4>
              <p className="text-sm text-muted-foreground">Market-driven order book ensures competitive prices</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'listing',
    title: 'Creating Your First Listing',
    description: 'List items for sale in just a few steps',
    icon: Package,
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Selling on SwapX is simple. Here's how to create your first listing:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Badge variant="secondary" className="mt-1">1</Badge>
            <div>
              <h4 className="font-medium">Set Up Payment Account</h4>
              <p className="text-sm text-muted-foreground">Connect your bank account to receive payments securely</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="secondary" className="mt-1">2</Badge>
            <div>
              <h4 className="font-medium">Upload Photos</h4>
              <p className="text-sm text-muted-foreground">Take clear photos of your item - our AI will analyze them</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="secondary" className="mt-1">3</Badge>
            <div>
              <h4 className="font-medium">Set Your Price</h4>
              <p className="text-sm text-muted-foreground">Choose to list at a specific price or sell instantly to the highest bidder</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="secondary" className="mt-1">4</Badge>
            <div>
              <h4 className="font-medium">Configure Shipping</h4>
              <p className="text-sm text-muted-foreground">Select package size for accurate shipping cost calculation</p>
            </div>
          </div>
        </div>
        <div className="p-3 bg-accent/50 rounded-lg border border-border">
          <p className="text-sm">
            <strong>Pro Tip:</strong> Use the "Sell" button in the navigation to get started!
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'trading',
    title: 'How Trading Works',
    description: 'Understanding the order book and making trades',
    icon: DollarSign,
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          SwapX uses an order book system similar to stock exchanges. Here's what you need to know:
        </p>
        <div className="space-y-3">
          <div>
            <h4 className="font-medium mb-2">Buy Orders (Bids)</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Buyers place orders at the price they're willing to pay. The highest bid gets priority.
            </p>
            <div className="p-2 bg-chart-2/10 rounded border border-chart-2/20 text-sm">
              Example: If you want to buy at $50, your order waits until a seller accepts that price
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Sell Orders (Asks)</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Sellers list items at their asking price. The lowest ask gets priority.
            </p>
            <div className="p-2 bg-chart-1/10 rounded border border-chart-1/20 text-sm">
              Example: If you list at $55, buyers can purchase at that price or make a lower bid
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Instant Trading</h4>
            <p className="text-sm text-muted-foreground">
              Want to trade immediately? Use a market order to match with the best available price.
            </p>
          </div>
        </div>
        <div className="p-3 bg-accent/50 rounded-lg border border-border">
          <p className="text-sm">
            <strong>Remember:</strong> You can view the order book and recent trades on any item's ticker page!
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'escrow',
    title: 'Understanding Delayed Disbursement',
    description: 'How we keep your transactions safe',
    icon: Shield,
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Every transaction on SwapX is protected by our delayed disbursement system. Here's how it works:
        </p>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">1</span>
            </div>
            <div>
              <h4 className="font-medium">Payment Held</h4>
              <p className="text-sm text-muted-foreground">
                When you buy, your payment is securely held - the seller doesn't get it yet
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">2</span>
            </div>
            <div>
              <h4 className="font-medium">Item Shipped</h4>
              <p className="text-sm text-muted-foreground">
                Seller marks the item as shipped and provides tracking information
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-primary">3</span>
            </div>
            <div>
              <h4 className="font-medium">Delivery Confirmed</h4>
              <p className="text-sm text-muted-foreground">
                You confirm receipt or funds are automatically released after 5 days
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-chart-2/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-4 w-4 text-chart-2" />
            </div>
            <div>
              <h4 className="font-medium">Funds Released</h4>
              <p className="text-sm text-muted-foreground">
                Payment is transferred to the seller's account
              </p>
            </div>
          </div>
        </div>
        <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
          <p className="text-sm">
            <strong>Issues?</strong> If something goes wrong, you can open a dispute and our team will help resolve it fairly.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'Start trading with confidence',
    icon: CheckCircle,
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Congratulations! You now know the basics of SwapX. Here's what to do next:
        </p>
        <div className="grid gap-3">
          <Button variant="default" className="w-full justify-start" onClick={() => window.location.href = '/sell'}>
            <Package className="mr-2 h-4 w-4" />
            Create Your First Listing
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/markets'}>
            <DollarSign className="mr-2 h-4 w-4" />
            Browse the Marketplace
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/profile'}>
            <Shield className="mr-2 h-4 w-4" />
            Set Up Your Profile
          </Button>
        </div>
        <div className="p-4 bg-gradient-to-br from-primary/10 to-chart-1/10 rounded-lg border border-primary/20">
          <h4 className="font-medium mb-2">Need Help?</h4>
          <p className="text-sm text-muted-foreground mb-3">
            You can always restart this tutorial from your profile settings, or contact our support team.
          </p>
          <Badge variant="secondary">
            Tutorial completed! 🎉
          </Badge>
        </div>
      </div>
    )
  }
];

export const OnboardingTutorial = () => {
  const { isOpen, currentStep, closeOnboarding, nextStep, prevStep, skipOnboarding } = useOnboarding();

  const step = ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const Icon = step?.icon;

  const handleFinish = async () => {
    await skipOnboarding();
  };

  if (!step) return null;

  return (
    <Dialog open={isOpen} onOpenChange={closeOnboarding}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={skipOnboarding}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            {Icon && (
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon className="h-6 w-6 text-primary" />
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-2xl">{step.title}</DialogTitle>
              <DialogDescription>{step.description}</DialogDescription>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {currentStep + 1} of {ONBOARDING_STEPS.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </DialogHeader>

        <div className="py-6">
          {step.content}
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t">
          <Button
            variant="ghost"
            onClick={skipOnboarding}
            className="text-muted-foreground"
          >
            Skip Tutorial
          </Button>
          
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={prevStep}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            
            {isLastStep ? (
              <Button onClick={handleFinish}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Finish
              </Button>
            ) : (
              <Button onClick={nextStep}>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
